import React, { useState, useEffect } from 'react';
import './WikipediaImageIndexer.css';

interface WikipediaImage {
  url: string;
  title: string;
  width?: number;
  height?: number;
  size?: number;
}

interface WikipediaPage {
  pageid: number;
  title: string;
  extract: string;
  url: string;
  images: WikipediaImage[];
}

interface PostData {
  postId: string;
  userId: string;
  createdAt: string;
  editedAt: string;
  title: string;
  textContent: string;
  metaDescription: string;
  categories: string[];
  mediaType: string;
  mediaUrls: string[];
  userColonies: string[];
  sharedWithEveryone: boolean;
  coloniesSharedWith: string[];
  postReplyTo: string;
  linkToPage: string;
  hasNSFWContent: boolean;
  originalThirdPartlyImageURL: string;
}

const WikipediaImageIndexer: React.FC = () => {
  const [currentBatch, setCurrentBatch] = useState<WikipediaPage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<{[key: string]: WikipediaImage}>({});
  const [processingPosts, setProcessingPosts] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>('');
  const [loadedBatches, setLoadedBatches] = useState(0);

  const API_ENDPOINT = "https://api-d2cime47qa-uc.a.run.app/api/indexed-posts";
  const USER_ID = "your-user-id";
  const BATCH_SIZE = 20;

  // Fetch random page IDs using Wikipedia's random API
  const fetchRandomPageIds = async (count: number): Promise<number[]> => {
    try {
      // Wikipedia's random API can return multiple random pages in a single call
      const response = await fetch(
        `https://en.wikipedia.org/w/api.php?` +
        `action=query&` +
        `format=json&` +
        `origin=*&` +
        `list=random&` +
        `rnnamespace=0&` + // Main namespace only (articles)
        `rnlimit=${Math.min(count, 20)}` // Wikipedia limits to 20 per call
      );
      
      const data = await response.json();
      if (data.query && data.query.random) {
        const pageIds = data.query.random.map((page: any) => page.id);
        
        // If we need more than 20 pages, make additional calls
        if (count > 20) {
          const remainingCount = count - 20;
          const additionalIds = await fetchRandomPageIds(remainingCount);
          return [...pageIds, ...additionalIds];
        }
        
        return pageIds;
      }
      
      throw new Error('No random pages returned from API');
    } catch (error) {
      console.error('Error fetching random page IDs:', error);
      throw new Error('Failed to fetch random page IDs');
    }
  };

  const fetchWikipediaPages = async (pageIds: number[]): Promise<WikipediaPage[]> => {
    try {
      // Fetch page info
      const pageInfoResponse = await fetch(
        `https://en.wikipedia.org/api/rest_v1/page/summary/${pageIds.join('|')}`
      );
      
      // Since the REST API doesn't support multiple pages, we'll use the legacy API
      const apiUrl = `https://en.wikipedia.org/w/api.php?` +
        `action=query&` +
        `format=json&` +
        `origin=*&` +
        `pageids=${pageIds.join('|')}&` +
        `prop=extracts|images|info&` +
        `exintro=1&` +
        `explaintext=1&` +
        `exsectionformat=plain&` +
        `imlimit=50&` +
        `inprop=url`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (!data.query || !data.query.pages) {
        throw new Error('No pages found');
      }

      const pages: WikipediaPage[] = [];
      
      for (const pageId of Object.keys(data.query.pages)) {
        const page = data.query.pages[pageId];
        
        // Skip missing pages
        if (page.missing) continue;

        const wikipediaPage: WikipediaPage = {
          pageid: parseInt(pageId),
          title: page.title || 'Unknown Title',
          extract: page.extract || '',
          url: page.fullurl || `https://en.wikipedia.org/wiki/${encodeURIComponent(page.title)}`,
          images: []
        };

        // Fetch images for this page
        if (page.images && page.images.length > 0) {
          const imagePromises = page.images.slice(0, 10).map(async (img: any) => {
            try {
              const imageInfoUrl = `https://en.wikipedia.org/w/api.php?` +
                `action=query&` +
                `format=json&` +
                `origin=*&` +
                `titles=${encodeURIComponent(img.title)}&` +
                `prop=imageinfo&` +
                `iiprop=url|size`;

              const imageResponse = await fetch(imageInfoUrl);
              const imageData = await imageResponse.json();
              
              if (imageData.query && imageData.query.pages) {
                const imagePageId = Object.keys(imageData.query.pages)[0];
                const imagePage = imageData.query.pages[imagePageId];
                
                if (imagePage.imageinfo && imagePage.imageinfo[0]) {
                  const imageInfo = imagePage.imageinfo[0];
                  return {
                    url: imageInfo.url,
                    title: img.title,
                    width: imageInfo.width,
                    height: imageInfo.height,
                    size: imageInfo.size
                  };
                }
              }
            } catch (error) {
              console.error('Error fetching image info:', error);
            }
            return null;
          });

          const imageResults = await Promise.all(imagePromises);
          wikipediaPage.images = imageResults.filter(img => img !== null) as WikipediaImage[];
        }

        pages.push(wikipediaPage);
      }

      return pages;
    } catch (error) {
      console.error('Error fetching Wikipedia pages:', error);
      throw error;
    }
  };

  const loadNextBatch = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const pageIds = await fetchRandomPageIds(BATCH_SIZE);
      const pages = await fetchWikipediaPages(pageIds);
      
      // Filter out pages without images
      const pagesWithImages = pages.filter(page => page.images.length > 0);
      
      setCurrentBatch(pagesWithImages);
      setLoadedBatches(prev => prev + 1);
    } catch (err) {
      setError(`Failed to load batch: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const selectImage = (pageId: number, image: WikipediaImage) => {
    const key = `${pageId}-${image.title}`;
    setSelectedImages(prev => ({
      ...prev,
      [key]: image
    }));
  };

  const deselectImage = (pageId: number, image: WikipediaImage) => {
    const key = `${pageId}-${image.title}`;
    setSelectedImages(prev => {
      const newSelected = { ...prev };
      delete newSelected[key];
      return newSelected;
    });
  };

  const postToAPI = async (page: WikipediaPage, image: WikipediaImage) => {
    const postId = `wiki-${page.pageid}-${Date.now()}`;
    setProcessingPosts(prev => new Set(prev).add(postId));

    try {
      const postData: PostData = {
        postId,
        userId: USER_ID,
        createdAt: new Date().toISOString(),
        editedAt: new Date().toISOString(),
        title: page.title,
        textContent: page.extract.substring(0, 500) + (page.extract.length > 500 ? '...' : ''),
        metaDescription: page.extract.substring(0, 200),
        categories: ['Wikipedia', 'Knowledge', 'Reference'],
        mediaType: 'image',
        mediaUrls: [image.url],
        userColonies: [],
        sharedWithEveryone: true,
        coloniesSharedWith: [],
        postReplyTo: '',
        linkToPage: page.url,
        hasNSFWContent: false,
        originalThirdPartlyImageURL: image.url
      };

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove from selected images after successful post
      deselectImage(page.pageid, image);
      
    } catch (error) {
      console.error('Error posting to API:', error);
      setError(`Failed to post ${page.title}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const isImageSelected = (pageId: number, image: WikipediaImage) => {
    const key = `${pageId}-${image.title}`;
    return key in selectedImages;
  };

  useEffect(() => {
    loadNextBatch();
  }, []);

  return (
    <div className="wikipedia-indexer-scroll">
        <div className="wikipedia-indexer">
        <div className="control-panel">
            <h2>Wikipedia Image Indexer</h2>
            <div className="batch-controls">
            <button 
                onClick={loadNextBatch} 
                disabled={isLoading}
                className="load-batch-btn"
            >
                {isLoading ? 'Loading...' : `Load ${BATCH_SIZE} Random Pages`}
            </button>
            <span className="batch-info">
                Batches loaded: {loadedBatches} (Random Wikipedia articles)
            </span>
            </div>
            
            {error && (
            <div className="error-message">
                {error}
            </div>
            )}
            
            <div className="selected-count">
            Selected images: {Object.keys(selectedImages).length}
            </div>
        </div>

        <div className="pages-grid">
            {currentBatch.map(page => (
            <div key={page.pageid} className="page-card">
                <div className="page-header">
                <h3>
                    <a href={page.url} target="_blank" rel="noopener noreferrer">
                    {page.title}
                    </a>
                </h3>
                <span className="page-id">ID: {page.pageid}</span>
                </div>
                
                <div className="page-extract">
                {page.extract.substring(0, 200)}...
                </div>
                
                <div className="images-section">
                <h4>Images ({page.images.length})</h4>
                <div className="images-grid">
                    {page.images.map((image, index) => (
                    <div 
                        key={`${page.pageid}-${index}`} 
                        className={`image-item ${isImageSelected(page.pageid, image) ? 'selected' : ''}`}
                    >
                        <img 
                        src={image.url} 
                        alt={image.title}
                        className="preview-image"
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                        />
                        <div className="image-info">
                        <div className="image-title">{image.title}</div>
                        {image.width && image.height && (
                            <div className="image-dimensions">
                            {image.width}×{image.height}
                            </div>
                        )}
                        {image.size && (
                            <div className="image-size">
                            {Math.round(image.size / 1024)}KB
                            </div>
                        )}
                        </div>
                        <div className="image-actions">
                        {isImageSelected(page.pageid, image) ? (
                            <>
                            <button 
                                onClick={() => deselectImage(page.pageid, image)}
                                className="deselect-btn"
                            >
                                Deselect
                            </button>
                            <button 
                                onClick={() => postToAPI(page, image)}
                                className="post-btn"
                                disabled={processingPosts.has(`wiki-${page.pageid}-${Date.now()}`)}
                            >
                                {processingPosts.has(`wiki-${page.pageid}-${Date.now()}`) ? 'Posting...' : 'Post'}
                            </button>
                            </>
                        ) : (
                            <button 
                            onClick={() => selectImage(page.pageid, image)}
                            className="select-btn"
                            >
                            Select
                            </button>
                        )}
                        </div>
                    </div>
                    ))}
                </div>
                </div>
            </div>
            ))}
        </div>
        </div>
    </div>
  );
};

export default WikipediaImageIndexer;